import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Users, IndianRupee, TrendingUp, Ticket, Activity } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#7A00EB','#00c3ef','#f59e0b','#ef4444','#10b981','#6366f1','#ec4899','#8b5cf6','#14b8a6','#f97316','#06b6d4','#84cc16'];

export function AdminDashboardTab({ analytics }) {
  const [realtime, setRealtime] = useState({ active_users: 0, by_page: {}, by_device: {}, sessions: [] });

  const loadRealtime = useCallback(() => {
    fetch(`${API_URL}/api/admin/analytics/realtime`).then(r => r.json()).then(setRealtime).catch(() => {});
  }, []);

  useEffect(() => {
    loadRealtime();
    const interval = setInterval(loadRealtime, 10000);
    return () => clearInterval(interval);
  }, [loadRealtime]);

  if (!analytics) return null;
  const pieData = [
    { name: 'Paid', value: analytics.paid_reports },
    { name: 'Free', value: analytics.free_reports },
  ];
  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Live Users Banner */}
      <div className="rounded-xl p-4 flex items-center gap-4" data-testid="live-users-banner"
        style={{ background: 'linear-gradient(to right, #7A00EB, #00c3ef)' }}>
        <div className="relative">
          <Activity className="h-8 w-8 text-white" />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-ping" />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full" />
        </div>
        <div className="flex-1">
          <p className="text-white/80 text-sm font-medium">Live Users on Quiz</p>
          <p className="text-white text-3xl font-bold" data-testid="live-user-count">{realtime.active_users}</p>
        </div>
        {Object.keys(realtime.by_page || {}).length > 0 && (
          <div className="hidden sm:flex gap-4">
            {Object.entries(realtime.by_page).map(([page, count]) => (
              <div key={page} className="text-center">
                <p className="text-white text-lg font-bold">{count}</p>
                <p className="text-white/60 text-xs capitalize">{page}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Submissions</CardTitle>
            <Users className="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent><div className="text-3xl font-bold">{analytics.total_submissions}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Revenue</CardTitle>
            <IndianRupee className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">₹{(analytics.total_revenue_paise / 100).toLocaleString()}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Conversion Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" style={{ color: '#7A00EB' }}>{analytics.conversion_rate}%</div>
            <p className="text-xs text-gray-500 mt-1">{analytics.paid_reports} paid / {analytics.total_submissions} total</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Coupons Redeemed</CardTitle>
            <Ticket className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent><div className="text-3xl font-bold text-amber-600">{analytics.total_coupon_usages}</div></CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-base">Submissions (Last 30 Days)</CardTitle></CardHeader>
          <CardContent>
            {analytics.submissions_over_time.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={analytics.submissions_over_time}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip labelFormatter={(v) => new Date(v).toLocaleDateString()} />
                  <Bar dataKey="count" fill="#7A00EB" radius={[4,4,0,0]} name="Submissions" />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-center text-gray-400 py-12">No data yet</p>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Revenue (Last 30 Days)</CardTitle></CardHeader>
          <CardContent>
            {analytics.revenue_over_time.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={analytics.revenue_over_time}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${v / 100}`} />
                  <Tooltip labelFormatter={(v) => new Date(v).toLocaleDateString()} formatter={(v) => [`₹${(v/100).toLocaleString()}`, 'Revenue']} />
                  <Line type="monotone" dataKey="amount" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981' }} name="Revenue" />
                </LineChart>
              </ResponsiveContainer>
            ) : <p className="text-center text-gray-400 py-12">No revenue data yet</p>}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="text-base">Archetype Distribution</CardTitle></CardHeader>
          <CardContent>
            {analytics.archetype_distribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analytics.archetype_distribution} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="archetype" tick={{ fontSize: 12 }} width={90} />
                  <Tooltip />
                  <Bar dataKey="count" name="Users" radius={[0,4,4,0]}>
                    {analytics.archetype_distribution.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-center text-gray-400 py-12">No data yet</p>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Paid vs Free Reports</CardTitle></CardHeader>
          <CardContent>
            {analytics.total_reports > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="45%" innerRadius={55} outerRadius={90} paddingAngle={4} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                    <Cell fill="#7A00EB" />
                    <Cell fill="#e5e7eb" />
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-center text-gray-400 py-12">No reports yet</p>}
          </CardContent>
        </Card>
      </div>

      {analytics.top_coupons.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Top Coupons Used</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {analytics.top_coupons.map((c) => (
                <div key={c.code} className="flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-3">
                  <span className="font-mono font-bold text-sm">{c.code}</span>
                  <span className="text-sm text-gray-500">{c.usage} uses</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
